import { ElementRef, ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { NonAuthenticatedModule } from './non-authenticated/non-authenticated.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { AuthInterceptor } from './dashboard/interceptors/auth.interceptor';
import { TranslateService } from '@ngx-translate/core';
import { TitleStrategyService } from './shared/services/title/title-strategy.service';
import { Router, TitleStrategy } from '@angular/router';
import {
  CldrIntlService,
  IntlModule,
  IntlService,
} from '@progress/kendo-angular-intl';
import { MessageService } from '@progress/kendo-angular-l10n';
import { MyMessageService } from './my-message.service';
import { ChartsModule } from '@progress/kendo-angular-charts';
import 'hammerjs';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { registerLocaleData } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { HttpErrorInterceptor } from './dashboard/interceptors/httperror.interceptor';
import { NOTIFICATION_CONTAINER } from '@progress/kendo-angular-notification';
import {
  AUTHENTICATED_URL,
  TranslationLoader,
  UNAUTHENTICATED_URL,
} from './shared/services/translation/translation-loader';

@NgModule({
  imports: [
    AppComponent,
    BrowserModule,
    NonAuthenticatedModule,
    BrowserAnimationsModule,
    IntlModule,
    ChartsModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: TitleStrategy,
      useClass: TitleStrategyService,
    },
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAppInitializer(() => {
        // Creates the TraceService so Sentry's router instrumentation starts.
        inject(Sentry.TraceService);
        const initializerFn = (appInitializerFactory)(inject(TranslateService), inject(CookieService));
        return initializerFn();
      }),
    CldrIntlService,
    {
      provide: IntlService,
      useExisting: CldrIntlService,
    },
    {
      provide: MessageService,
      useFactory: messageFactory,
      deps: [HttpClient, CookieService],
    },
    [{ provide: ICON_SETTINGS, useValue: { type: 'font' } }],
    JwtHelperService,
    CookieService,
    TranslationLoader,
    {
      provide: UNAUTHENTICATED_URL,
      useValue: 'api/v2/Language/GetPreLoginTranslations?language=',
    },
    {
      provide: AUTHENTICATED_URL,
      useValue:
        'api/v2/Language/GetTranslationsByDictionary?dictionaryId=52B54DAD-3FA6-426F-B261-D20244634057&language=',
    },
    {
      provide: NOTIFICATION_CONTAINER,
      useFactory: () => {
        //return the container ElementRef, where the notification will be injected
        return { nativeElement: document.body } as ElementRef;
      },
    },
  ],
})
export class AppModule {}

export function messageFactory(
  httpClient: HttpClient,
  cookieService: CookieService,
) {
  return new MyMessageService(httpClient, cookieService);
}

export function appInitializerFactory(
  translate: TranslateService,
  cookieService: CookieService,
) {
  return async () => {
    const language = cookieService.get('language');
    translate.setDefaultLang(language);
    await translate.use(language).toPromise();

    try {
      // register locale for pipes
      const baseLocale = language.split('-')[0];
      let localeModule;

      /* @vite-ignore */
      switch (baseLocale) {
        case 'en':
          localeModule = await import('@angular/common/locales/en');
          break;
        case 'de':
          localeModule = await import('@angular/common/locales/de');
          break;
        case 'nl':
          localeModule = await import('@angular/common/locales/nl');
          break;
        default:
          localeModule = await import('@angular/common/locales/en');
      }

      registerLocaleData(localeModule.default, language);
    } catch (error) {
      console.warn('Translation file error found for Angular pipes:', error);
    }

    await Promise.resolve();
  };
}
